import { useState } from 'react';
import useWindowSize from '../hooks/useWindowSize';

const MobileOptimizedInput = ({
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  label,
  error,
  required = false,
  disabled = false,
  autoComplete,
  inputMode,
  pattern,
  maxLength,
  minLength,
  className = '',
  style = {},
  ...props
}) => {
  const { isMobile } = useWindowSize();
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const baseInputStyle = {
    width: '100%',
    padding: isMobile ? '16px 20px' : '12px 16px',
    fontSize: isMobile ? '16px' : '14px', // 16px previene zoom en iOS
    lineHeight: '1.5',
    border: `2px solid ${error ? '#dc3545' : isFocused ? '#4352A9' : '#e1e5e9'}`,
    borderRadius: '8px',
    backgroundColor: disabled ? '#f8f9fa' : '#ffffff',
    color: disabled ? '#6c757d' : '#212529',
    transition: 'all 0.3s ease',
    outline: 'none',
    fontFamily: 'inherit',
    minHeight: isMobile ? '48px' : '40px', // Área táctil mínima recomendada
    boxSizing: 'border-box',
    ...style,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: isMobile ? '16px' : '14px',
    fontWeight: '600',
    color: error ? '#dc3545' : '#495057',
    lineHeight: '1.4',
  };

  const errorStyle = {
    display: 'block',
    marginTop: '6px',
    fontSize: isMobile ? '14px' : '12px',
    color: '#dc3545',
    lineHeight: '1.4',
  };

  const containerStyle = {
    position: 'relative',
    marginBottom: '20px',
    width: '100%',
  };

  const passwordToggleStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6c757d',
    fontSize: '18px',
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'color 0.3s ease',
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (props.onFocus) props.onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (props.onBlur) props.onBlur(e);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Determinar el tipo de input basado en si es password y si se está mostrando
  const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

  // Optimizaciones específicas para móviles
  const mobileProps = isMobile ? {
    autoCapitalize: type === 'email' ? 'none' : 'sentences',
    autoCorrect: type === 'email' || type === 'password' ? 'off' : 'on',
    spellCheck: type === 'email' || type === 'password' ? 'false' : 'true',
  } : {};

  return (
    <div style={containerStyle} className={className}>
      {label && (
        <label htmlFor={name} style={labelStyle}>
          {label}
          {required && <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <input
          id={name}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          inputMode={inputMode}
          pattern={pattern}
          maxLength={maxLength}
          minLength={minLength}
          style={baseInputStyle}
          {...mobileProps}
          {...props}
        />
        
        {type === 'password' && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            style={passwordToggleStyle}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            tabIndex={-1}
          >
            <span className="material-icons">
              {showPassword ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        )}
      </div>
      
      {error && (
        <span style={errorStyle} role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default MobileOptimizedInput;
